/**
 * BookingMessagesTab Component
 *
 * Displays SMS and WhatsApp message delivery history for a booking.
 * Shows a combined view with channel filter toggle.
 */

import { useState, useEffect } from 'react';
import {
  MessageSquare,
  Smartphone,
  ChevronDown,
  ChevronUp,
  Loader2,
  AlertCircle,
  Clock,
  CheckCircle,
  Send,
  Eye,
  AlertTriangle,
  XCircle,
  RefreshCw,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  getMessageSendLogs,
  getMessageStatusBadgeClass,
  formatMessageStatus,
  type MessageSendLog,
  type MessageChannel,
} from '../services/messageSendLog.service';
import { useDateFormatter } from '../hooks/useDateFormatter';

interface BookingMessagesTabProps {
  bookingId: string;
  smsEnabled: boolean;
  whatsappEnabled: boolean;
}

// Icon mapping for message statuses
const statusIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  pending: Clock,
  sent: Send,
  accepted: Send,
  delivered: CheckCircle,
  read: Eye,
  failed: XCircle,
  undelivered: AlertTriangle,
};

export default function BookingMessagesTab({
  bookingId,
  smsEnabled,
  whatsappEnabled,
}: BookingMessagesTabProps) {
  const { t } = useTranslation();
  const { formatDateTimeCompact } = useDateFormatter();
  const [messageLogs, setMessageLogs] = useState<MessageSendLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedLogs, setExpandedLogs] = useState<Set<string>>(new Set());
  const [channelFilter, setChannelFilter] = useState<MessageChannel | 'all'>(
    smsEnabled && whatsappEnabled ? 'all' : smsEnabled ? 'sms' : 'whatsapp'
  );

  useEffect(() => {
    loadMessageLogs();
  }, [bookingId, channelFilter]);

  async function loadMessageLogs() {
    setLoading(true);
    setError(null);

    const channel = channelFilter === 'all' ? undefined : channelFilter;
    const { data, error: loadError } = await getMessageSendLogs(bookingId, channel);

    if (loadError) {
      setError(loadError);
    } else {
      setMessageLogs(data || []);
    }

    setLoading(false);
  }

  const toggleExpanded = (logId: string) => {
    setExpandedLogs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(logId)) {
        newSet.delete(logId);
      } else {
        newSet.add(logId);
      }
      return newSet;
    });
  };

  const getChannelIcon = (channel: string) => {
    return channel === 'whatsapp' ? MessageSquare : Smartphone;
  };

  const getChannelLabel = (channel: string) => {
    return channel === 'whatsapp'
      ? t('bookingMessages.whatsapp')
      : t('bookingMessages.sms');
  };

  const getChannelColor = (channel: string) => {
    return channel === 'whatsapp' ? 'text-green-600' : 'text-blue';
  };

  const getChannelBgColor = (channel: string) => {
    return channel === 'whatsapp' ? 'bg-green-600/10' : 'bg-blue/10';
  };

  const formatTimestamp = (timestamp: string): string => formatDateTimeCompact(timestamp);

  const showChannelFilter = smsEnabled && whatsappEnabled;

  return (
    <div className="space-y-lg">
      {/* Channel Filter */}
      {showChannelFilter && (
        <div className="flex gap-sm">
          <button
            onClick={() => setChannelFilter('all')}
            className={`px-md py-xs text-sm font-medium rounded-full transition-colors ${
              channelFilter === 'all'
                ? 'bg-blue text-white'
                : 'bg-secondary/20 text-secondary hover:text-primary'
            }`}
          >
            {t('bookingMessages.allChannels')}
          </button>
          <button
            onClick={() => setChannelFilter('sms')}
            className={`px-md py-xs text-sm font-medium rounded-full transition-colors ${
              channelFilter === 'sms'
                ? 'bg-blue text-white'
                : 'bg-secondary/20 text-secondary hover:text-primary'
            }`}
          >
            SMS
          </button>
          <button
            onClick={() => setChannelFilter('whatsapp')}
            className={`px-md py-xs text-sm font-medium rounded-full transition-colors ${
              channelFilter === 'whatsapp'
                ? 'bg-green-600 text-white'
                : 'bg-secondary/20 text-secondary hover:text-primary'
            }`}
          >
            WhatsApp
          </button>
        </div>
      )}

      {/* Message History Section */}
      <div className="space-y-md">
        <div className="flex items-center gap-md">
          <h3 className="text-lg font-semibold text-primary">{t('bookingMessages.messageHistory')}</h3>
          <button
            onClick={loadMessageLogs}
            disabled={loading}
            className="p-1 text-secondary hover:text-primary hover:bg-secondary rounded-full transition-colors"
            title={t('bookingMessages.refreshMessageHistory')}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-xl">
            <Loader2 className="w-6 h-6 text-blue animate-spin" />
            <span className="ml-md text-secondary">{t('bookingMessages.loadingMessageHistory')}</span>
          </div>
        ) : error ? (
          <div className="p-lg bg-danger/10 border border-danger/20 rounded-lg">
            <div className="flex items-center gap-md">
              <AlertCircle className="w-5 h-5 text-danger flex-shrink-0" />
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-danger mb-xs">{t('bookingMessages.errorLoadingMessageHistory')}</h4>
                <p className="text-sm text-danger/80">{error}</p>
              </div>
              <button
                onClick={loadMessageLogs}
                className="btn btn-ghost text-danger hover:bg-danger/10"
              >
                {t('common.retry')}
              </button>
            </div>
          </div>
        ) : messageLogs.length === 0 ? (
          <div className="text-center py-xl border border-border rounded-lg bg-secondary/20">
            <div className="w-12 h-12 rounded-full bg-tertiary mx-auto mb-md flex items-center justify-center">
              <MessageSquare className="w-6 h-6 text-secondary" />
            </div>
            <p className="text-secondary text-sm">
              {t('bookingMessages.noMessagesSent')}
            </p>
          </div>
        ) : (
          <div className="space-y-md">
            {messageLogs.map((log) => {
              const ChannelIcon = getChannelIcon(log.channel);
              const channelColor = getChannelColor(log.channel);
              const channelBg = getChannelBgColor(log.channel);
              const isExpanded = expandedLogs.has(log.id);
              const StatusIcon = statusIcons[log.status.toLowerCase()] || Clock;

              return (
                <div
                  key={log.id}
                  className="border border-border rounded-lg overflow-hidden"
                >
                  {/* Message Header */}
                  <div className="p-md bg-secondary/5">
                    <div className="flex items-start justify-between gap-md">
                      <div className="flex items-start gap-md">
                        <div className={`w-10 h-10 rounded-full ${channelBg} flex items-center justify-center flex-shrink-0`}>
                          <ChannelIcon className={`w-5 h-5 ${channelColor}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-md mb-xs">
                            <h4 className="font-semibold text-primary">
                              {getChannelLabel(log.channel)}
                            </h4>
                            <span className={`badge ${getMessageStatusBadgeClass(log.status)}`}>
                              {formatMessageStatus(log.status)}
                            </span>
                          </div>
                          <p className="text-sm text-secondary">
                            {t('bookingMessages.to')}: {log.recipient}
                          </p>
                          <div className="flex flex-wrap items-center gap-md mt-xs">
                            <p className="text-xs text-secondary/70">
                              {t('bookingMessages.scheduled')}: {formatTimestamp(log.target_send_at)}
                            </p>
                            {log.provider && (
                              <p className="text-xs text-secondary/70">
                                {t('bookingMessages.via')} {log.provider}
                              </p>
                            )}
                          </div>
                          {log.error_message && (
                            <p className="text-xs text-danger mt-xs">
                              {log.error_message}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-xs">
                        <StatusIcon className={`w-4 h-4 ${
                          log.status === 'failed' || log.status === 'undelivered'
                            ? 'text-danger'
                            : log.status === 'delivered' || log.status === 'read'
                            ? 'text-success'
                            : 'text-secondary'
                        }`} />
                      </div>
                    </div>

                    {/* Expandable content toggle */}
                    {log.resolved_text && (
                      <button
                        onClick={() => toggleExpanded(log.id)}
                        className="flex items-center gap-xs text-sm text-blue hover:text-blue/80 mt-md transition-colors"
                      >
                        {isExpanded ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                        {isExpanded ? t('bookingMessages.hideMessage') : t('bookingMessages.showMessage')}
                      </button>
                    )}
                  </div>

                  {/* Expanded Message Content */}
                  {isExpanded && log.resolved_text && (
                    <div className="p-md border-t border-border bg-primary">
                      <p className="text-sm text-primary whitespace-pre-wrap">{log.resolved_text}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
